import { MainFooterProps } from '@engyalo/delivery-ui-components/lib/ui/components/MainFooter'
import { DefaultProps } from '../../../interfaces'
import deepMerge from '../../../utils/deepMerge'
import {
  DefaultModalContainerNames,
  CinnamonActionNames,
  CinnamonPaths,
  TextLibrary,
  TemplateActionNames,
  CinnamonVariables,
} from '../defaultConstants'
import {
  CounterProductDetail,
  CounterRecommendedDetail,
  CounterProductDetailBundle,
} from './childProps/Counter'
import { PriceSimplified } from './childProps/Price'
import { PromotionSelectionCriteria } from '../../../graphQL/commons'
import { ButtonCartPriceDetailed } from './Button'

const bundleButtonClickArgs = {
  product: '{{catalog.selectedProduct}}',
  value: '{{catalog.currentBundleCount}}',
  newValue: '{{catalog.currentBundleCount}}',
  currency:
    '{{config.options.currencyPrefix}} || {{config.options.currencyCode}}',
  isBundle: '{{catalog.selectedProduct.isBundle}}',
  newSubItems: '{{catalog.bundlesCount}}',
  currentSubItems: '{{catalog.subItemsList}}',
  currentRoute: 'catalog',
}

export const MainFooterDefault: DefaultProps<MainFooterProps> = {
  button: {
    label: TextLibrary.CART_GO_TO_BUTTON,
    loading: '{{catalog.synchronizingCart}}' as any,
    startLabel: {
      label: '{{cart.itemsTotal}}',
      counter: true,
    },
    endLabel: {
      price: {
        ...PriceSimplified,
        display: '{{globals.footer.priceDisplay}}',
        value: '{{cart.subtotal}}' as any,
        discount: '{{cart.total}}' as any,
        fee: '{{cart.totalFee}}' as any,
        feeDisplay: '{{globals.footer.feeDisplay}}',
        vertical: true,
        feeColor: 'primary.contrastText',
        discountColor: 'primary.contrastText',
        gap: 2,
        align: '{{globals.footer.priceAlign}}' as any,
        styles: {
          '& .PriceFee': {
            marginTop: 0,
          },
        },
      },
    },
    actions: {
      cinnamonActions: [
        {
          name: CinnamonActionNames.PUSH_VIEW,
          args: {
            path: CinnamonPaths.CART,
          },
        },
      ],
    } as any,
  },
}

export const MainFooterBaseProductDetail = deepMerge(MainFooterDefault, {
  counter: CounterProductDetail,
  button: {
    label: TextLibrary.CART_GO_TO_BUTTON,
    startLabel: undefined,
    loading: '{{catalog.synchronizingCart}}' as any,
  },
  bundleData: {
    counter: CounterProductDetailBundle,
    isBundle: '{{catalog.selectedProduct.isBundle}}' as any,
    button: {
      disabled: '{{catalog.disabledAddOnBundle}}' as any,
      label: `${TextLibrary.CART_ADD_TO_CART}{{catalog.currentBundlePrice}}`,
      startLabel: undefined,
      loading: '{{catalog.synchronizingCart}}' as any,
      onClick: {
        cinnamonActions: [
          {
            name: CinnamonActionNames.ADD_ITEM_TO_CART,
            args: bundleButtonClickArgs,
          },
          {
            name: CinnamonActionNames.CLOSE_MODAL,
            args: {
              container: DefaultModalContainerNames.PRODUCT_DETAIL,
            },
          },
          {
            name: CinnamonActionNames.CLEAR_SUBITEMS,
          },
        ],
      } as any,
    },
  },
  hideCounterFields: ['extraPackages'],
  product: '{{catalog.selectedProduct}}' as any,
})

export const MainFooterProductDetail = deepMerge(MainFooterBaseProductDetail, {
  fullWidth: false,
  resumeData: {
    title: TextLibrary.PRODUCT_TOTAL_AMOUNT,
    amount: {
      ...PriceSimplified,
      value: '{{catalog.selectedProduct.subtotal}}' as any,
    },
  },
  isCounterOnTop: true,
  button: ButtonCartPriceDetailed,
  bundleData: {
    button: {
      label: `${TextLibrary.CART_ADD_TO_CART}`,
      onClick: {
        cinnamonActions: [
          {
            name: CinnamonActionNames.ADD_ITEM_TO_CART,
            args: bundleButtonClickArgs,
          },
          {
            name: CinnamonActionNames.GO_BACK,
          },
          {
            name: CinnamonActionNames.CLEAR_SUBITEMS,
          },
        ],
      } as any,
    },
    resumeData: {
      title: TextLibrary.PRODUCT_TOTAL_AMOUNT,
      amount: {
        ...PriceSimplified,
        value: '{{catalog.currentBundlePriceValue}}' as any,
      },
    },
  },
})

export const MainFooterProductDetailModal = deepMerge(
  MainFooterBaseProductDetail,
  {
    fullWidth: false,
    onlyShowCounterOnDataReady: true,
    product: '{{catalog.selectedProduct}}' as any,
    button: {
      label: TextLibrary.CART_GO_TO_BUTTON,
      actions: {
        cinnamonActions: [
          {
            name: CinnamonActionNames.PUSH_VIEW,
            args: {
              path: CinnamonPaths.CART,
            },
          },
          {
            name: CinnamonActionNames.CLOSE_MODAL,
            args: {
              container: DefaultModalContainerNames.PRODUCT_DETAIL,
            },
          },
        ],
      } as any,
    },
  },
)

export const MainFooterCart = deepMerge(MainFooterDefault, {
  button: {
    label: TextLibrary.CART_PLACE_BUTTON,
    color: 'success',
    /**
     * Two reasons the shopper cannot place an order yet, ORed.
     *
     * `cart.disablePlaceOrder` is the long-standing one: an empty cart, or a
     * `configuration.checkoutRules` condition the cart does not meet.
     * `checkout.rulesResolving` is COM-1048's, and it holds the button while
     * a one-option checkout rule settles itself. Without it a shopper taps
     * before the write lands, and is either routed to a picker they should
     * never see or creates an order with no selection recorded.
     *
     * `MainFooterSummary` and `MainFooterCartPriceDetailed` both
     * `deepMerge` this object and neither re-authors `disabled`, so this one
     * line holds the cart footer and the summary footer that places the order.
     *
     * ⚠️ **This is the repo's first `||` between two non-constant sources,
     * and that is what keeps it working.** `composeResult`'s operator branch
     * is guarded by `(!initial || variables.some((v) => v.constant))`, and
     * neither `cart` nor `checkout` is in `constantSources` — so
     * `processConfig`'s boot pass skips the branch and the string survives
     * intact for `useCinnamonProps` to resolve at runtime. Adding any
     * `{{config.*}}`, `{{globals.*}}` or `{{env.*}}` token here flips that
     * guard and evaluates the whole OR at boot, against a store that does not
     * exist yet. `CinnamonVariables.WORKFLOW_ID` is the live instance of that
     * shape. The ` || ` spacing is load-bearing too: the split is
     * `/( \|\| | \?\? )/g`. All four behaviours are characterised in
     * `utils/tests/processData.test.ts`.
     *
     * ⚠️ **Not a loader.** `routes/index.tsx` counts every loader but
     * `CREATE_ORDER` as booting, and while booting `<Routes>` registers
     * nothing but the catch-all — so raising one to hold this button would
     * unmount the cart that the hook raising it is mounted on. COM-858's
     * failure class, met again in COM-974.
     */
    disabled:
      '{{cart.disablePlaceOrder}} || {{checkout.rulesResolving}}' as any,
    disabledOnLoading: true,
    actions: {
      cinnamonActions: [
        {
          name: TemplateActionNames.PLACE_ORDER_FLOW,
          args: {
            actions: '{{globals.placeOrderFlow.actions}}',
            pushAfter: '{{globals.placeOrderFlow.pushAfter}}',
          },
        },
      ],
    } as any,
  },
})

export const MainFooterCartPriceDetailed = deepMerge(MainFooterCart, {
  fullWidth: false,
  button: ButtonCartPriceDetailed,
})

export const MainFooterSummary = deepMerge(MainFooterCart, {
  button: {
    /**
     * Two captions for one button, so the binding cannot be a text token.
     *
     * `TextLibrary.SUMMARY_CONTINUE_BUTTON` was here. It resolved on
     * `processConfig`'s boot pass and baked into the props, because `texts` is
     * a constant source — which is exactly what makes it unusable for a label
     * that has to change under the shopper's hands. `checkoutSlice.ctaLabel`
     * carries *a token* instead, seeded to that same member, and
     * `useSummaryCheckoutCta` swaps it for
     * `TextLibrary.SUMMARY_CONTINUE_TO_SELECTION` while a checkout rule still
     * needs choosing. `processData` re-resolves a resolved string that still
     * contains `{{`, so both hops land on the storefront's own copy.
     *
     * ⚠️ Not `'{{checkout.ctaLabel}} || {{texts.summary.continue}}'`. That
     * flips `composeResult`'s `variables.some((v) => v.constant)` guard and
     * evaluates the whole OR at boot against a store that does not exist,
     * baking the fallback in permanently. Same trap the `disabled` line above
     * documents, and the reason the seed lives in the slice rather than here.
     *
     * A storefront that re-authors `label` in its own config body keeps its
     * copy and never sees *Continuar* — the same class as the `disabled`
     * warning above.
     */
    label: '{{checkout.ctaLabel}}' as any,
    /**
     * One action that decides, not `CREATE_ORDER` bare.
     *
     * ⚠️ **This changes the summary screen for every storefront, M2 config or
     * not.** The button fired `CREATE_ORDER` directly since it was authored,
     * which skipped `placeOrderFlow` and with it the alcohol and returnables
     * gate `MainFooterCart` runs — so any storefront with either rule active
     * has been placing orders from this screen without the gate its own
     * config asked for. It now runs the queue, and those dialogs fire.
     *
     * Less obviously, the queue's tail is `useLoadDialogValidations`, whose
     * `else` branch opens `confirmOrderDialog` even when no validation
     * applies. **Every storefront that places from the summary gains one
     * confirmation tap it did not have.** For a shopper with no checkout rules
     * at all that is the most visible change in this ticket.
     *
     * ⚠️ **The action list is `summaryActions`, not `actions`.** Binding the
     * cart's list here stalls checkout — see `IGlobals.placeOrderFlow`. Its
     * tail still reaches `CREATE_ORDER` through the confirm dialog, which
     * passes the same five args this footer used to pass directly, so nothing
     * is lost.
     *
     * `pushAfter` stays the shared global. A storefront that authored none
     * keeps `''`, `usePlaceOrderFlow` pushes nowhere, and the legacy
     * `postCreateOrder` redirect takes the shopper out exactly as today.
     */
    actions: {
      cinnamonActions: [
        {
          name: TemplateActionNames.SUMMARY_CONTINUE_OR_PLACE_ORDER,
          args: {
            actions: '{{globals.placeOrderFlow.summaryActions}}',
            pushAfter: '{{globals.placeOrderFlow.pushAfter}}',
          },
        },
      ],
    } as any,
    styles: {
      '& .LabelEnd': {
        visibility: 'hidden',
      },
    },
  },
})

export const MainFooterFilters = deepMerge(MainFooterDefault, {
  button: {
    startLabel: undefined,
    endLabel: undefined,
    label: TextLibrary.FILTERS_APPLY_BUTTON_TEXT,
    actions: {
      cinnamonActions: [
        {
          name: CinnamonActionNames.CLOSE_MODAL,
          args: {
            container: DefaultModalContainerNames.FILTERS_LIST,
          },
        },
        {
          name: TemplateActionNames.FILTERS_GET_FILTERS_RESULTS,
        },
      ],
    } as any,
  },
})

export const MainFooterRecommendedProducts: DefaultProps<MainFooterProps> = {
  fullWidth: false,
  button: {
    label: '{{cart.recommendedProducts.closeButton}}',
    variant: 'outlined',
    color: 'inherit',
    loading: '{{catalog.synchronizingCart}}' as any,
    actions: {
      cinnamonActions: [
        {
          name: TemplateActionNames.CART_CLOSE_RECOMMENDED_PRODUCTS_MODAL,
          args: {
            container: DefaultModalContainerNames.RECOMMENDED_PRODUCTS,
            continue: true,
          },
        },
      ],
    } as any,
  },
  styles: {
    padding: '0 12px 16px',
  },
}

export const MainFooterPrefillCart = deepMerge(MainFooterDefault, {
  fullWidth: false,
  button: {
    color: 'success',
    label: TextLibrary.CART_ADD_TO_CART,
    startLabel: {
      label: '{{cart.prefillCart.quantity}}',
      counter: true,
    },
    endLabel: {
      price: {
        value: '{{cart.prefillCart.total}}' as any,
        discount: undefined,
      },
    },
    loading: '{{catalog.synchronizingCart}}' as any,
    disabled: '{{cart.prefillCart.disabled}}' as any,
    actions: {
      cinnamonActions: [
        {
          name: TemplateActionNames.PREFILL_CART_TO_CART,
        },
      ],
    } as any,
  },
})

export const MainFooterRecommendedProuctDetailModal = deepMerge(
  MainFooterProductDetailModal,
  {
    counter: CounterRecommendedDetail,
    button: {
      label: TextLibrary.RECOMMENDED_PRODUCTS_PROCEED,
      startLabel: undefined,
      endLabel: undefined,
      actions: {
        cinnamonActions: [
          {
            name: CinnamonActionNames.CLOSE_MODAL,
            args: {
              container: DefaultModalContainerNames.RECOMMENDED_PRODUCT_DETAIL,
            },
          },
          {
            name: TemplateActionNames.CART_UPDATE_RECOMMENDED_PRODUCT_BUTTON,
            args: {
              label: TextLibrary.RECOMMENDED_PRODUCTS_PROCEED,
            },
          },
        ],
      } as any,
    },
  },
)

export const MainFooterRecommendedProuctDetail = deepMerge(
  MainFooterRecommendedProuctDetailModal,
  {
    isCounterOnTop: true,
    resumeData: {
      title: TextLibrary.PRODUCT_TOTAL_AMOUNT,
      amount: {
        ...PriceSimplified,
        value: '{{catalog.selectedProduct.subtotal}}' as any,
      },
    },
    button: {
      ...ButtonCartPriceDetailed,
      actions: {
        cinnamonActions: [
          {
            name: CinnamonActionNames.GO_BACK,
          },
          {
            name: CinnamonActionNames.CLOSE_MODAL,
            args: {
              container: DefaultModalContainerNames.RECOMMENDED_PRODUCT_DETAIL,
            },
          },
          {
            name: TemplateActionNames.CART_UPDATE_RECOMMENDED_PRODUCT_BUTTON,
            args: {
              label: TextLibrary.RECOMMENDED_PRODUCTS_PROCEED,
            },
          },
        ],
      } as any,
    },
    bundleData: {
      button: {
        label: `${TextLibrary.CART_ADD_TO_CART}`,
        onClick: {
          cinnamonActions: [
            {
              name: CinnamonActionNames.ADD_ITEM_TO_CART,
              args: bundleButtonClickArgs,
            },
            {
              name: CinnamonActionNames.GO_BACK,
            },
            {
              name: CinnamonActionNames.CLEAR_SUBITEMS,
            },
          ],
        } as any,
      },
      resumeData: {
        title: TextLibrary.PRODUCT_TOTAL_AMOUNT,
        amount: {
          ...PriceSimplified,
          value: '{{catalog.currentBundlePriceValue}}' as any,
        },
      },
    },
  },
)

export const MainFooterLinkedProducts: DefaultProps<MainFooterProps> = {
  fullWidth: false,
  button: {
    label: CinnamonVariables.LINKED_PRODUCTS_BUTTON_LABEL,
    color: 'success',
    loading: '{{catalog.synchronizingCart}}' as any,
    disabledOnLoading: true,
    actions: {
      cinnamonActions: [
        {
          name: TemplateActionNames.LINKED_PRODUCTS_VALIDATE_STOCK,
        },
      ],
    } as any,
  },
  styles: {
    padding: '0 12px 16px',
  },
}

export const MainFooterChosenFreegoods: DefaultProps<MainFooterProps> = {
  fullWidth: false,
  button: {
    label: TextLibrary.CHOSEN_FREEGOODS_CONFIRM,
    color: 'primary',
    loading: '{{catalog.synchronizingCart}}' as any,
    disabled: '{{catalog.freegoodsConf.isConfirmDisabled}}' as any,
    disabledOnLoading: true,
    actions: {
      cinnamonActions: [
        {
          name: CinnamonActionNames.CONFIRM_CHOSEN_FREEGOODS,
          args: {
            selectBy: '{{catalog.freegoodsConf.selectBy}}' as any,
          },
        },
      ],
    } as any,
  },
  text: {
    value: TextLibrary.CHOSEN_FREEGOODS_REJECT,
    actions: {
      cinnamonActions: [
        {
          name: CinnamonActionNames.CONFIRM_CHOSEN_FREEGOODS,
          args: {
            selectBy: PromotionSelectionCriteria.REFUSED,
          },
        },
      ],
    } as any,
  },
  styles: {
    padding: '0 12px 16px',
  },
}
